'use client';

import {
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Pagination as PaginationRoot,
} from '@workspace/ui/components/pagination';
// import { useTranslations } from "next-intl";
import { cn } from '@workspace/ui/lib';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useThrottledCallback } from 'use-debounce';

interface CustomPaginationProps {
  totalItems: number;
  currentPage: number;
  itemsPerPage: number;
  mobileBreakpoint?: number;
  className?: string;
  onPageChange: (page: number) => void;
}

const PaginationComponent = ({
  totalItems,
  currentPage,
  className,
  itemsPerPage,
  mobileBreakpoint = 768,
  onPageChange,
}: CustomPaginationProps) => {
  // const t = useTranslations("pagination");

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const [isMobile, setIsMobile] = useState(false);

  // Create throttled handler for resize events
  const throttledResizeHandler = useThrottledCallback(() => {
    setIsMobile(window.innerWidth <= mobileBreakpoint);
  }, 1500); // Throttle to once every 1.5 seconds

  useEffect(() => {
    // Set initial state
    setIsMobile(window.innerWidth <= mobileBreakpoint);

    // Add throttled resize listener
    window.addEventListener('resize', throttledResizeHandler);
    return () => window.removeEventListener('resize', throttledResizeHandler);
  }, [throttledResizeHandler, mobileBreakpoint]);

  // Memoize page range calculations
  const { startPage, endPage } = useMemo(() => {
    let start = Math.max(2, currentPage <= 2 ? 2 : currentPage - 1);
    let end = Math.min(
      totalPages - 1,
      currentPage >= totalPages - 1 ? totalPages - 1 : currentPage + 1
    );

    if (currentPage <= 2) {
      end = Math.min(4, totalPages - 1);
    }
    if (currentPage >= totalPages - 1) {
      start = Math.max(2, totalPages - 3);
    }

    return { startPage: start, endPage: end };
  }, [currentPage, totalPages]);

  // Memoize middle pages calculation
  const middlePagesNeeded = useMemo(() => (isMobile ? 1 : 3), [isMobile]);

  // Memoize page change handlers
  const handlePageChange = useCallback(
    (page: number) => {
      // Ensure page is within valid bounds (1 to totalPages)
      const boundedPage = Math.min(Math.max(1, page), totalPages);
      onPageChange(boundedPage);
    },
    [onPageChange, totalPages]
  );

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  }, [currentPage, onPageChange]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  }, [currentPage, totalPages, onPageChange]);

  // Memoize mobile pages render function
  const renderMobilePages = useCallback(() => {
    // First page
    if (currentPage === 1) {
      return (
        <>
          <PaginationItem>
            <PaginationLink isActive={true} size="sm">
              1
            </PaginationLink>
          </PaginationItem>
          {totalPages > 1 && (
            <PaginationItem>
              <PaginationLink onClick={() => handlePageChange(2)} size="sm">
                2
              </PaginationLink>
            </PaginationItem>
          )}
          {totalPages > 2 && (
            <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
              <PaginationEllipsis />
            </PaginationItem>
          )}
          {totalPages > 2 && (
            <PaginationItem>
              <PaginationLink onClick={() => handlePageChange(totalPages)} size="sm">
                {totalPages}
              </PaginationLink>
            </PaginationItem>
          )}
        </>
      );
    }

    // Second page
    if (currentPage === 2) {
      return (
        <>
          <PaginationItem>
            <PaginationLink onClick={() => handlePageChange(1)} size="sm">
              1
            </PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationLink isActive={true} size="sm">
              2
            </PaginationLink>
          </PaginationItem>
          {totalPages > 2 && (
            <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
              <PaginationEllipsis />
            </PaginationItem>
          )}
          {totalPages > 2 && (
            <PaginationItem>
              <PaginationLink onClick={() => handlePageChange(totalPages)} size="sm">
                {totalPages}
              </PaginationLink>
            </PaginationItem>
          )}
        </>
      );
    }

    // Last page
    if (currentPage === totalPages) {
      return (
        <>
          <PaginationItem>
            <PaginationLink onClick={() => handlePageChange(1)} size="sm">
              1
            </PaginationLink>
          </PaginationItem>
          {totalPages > 2 && (
            <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
              <PaginationEllipsis />
            </PaginationItem>
          )}
          <PaginationItem>
            <PaginationLink onClick={() => handlePageChange(totalPages - 1)} size="sm">
              {totalPages - 1}
            </PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationLink isActive={true} size="sm">
              {totalPages}
            </PaginationLink>
          </PaginationItem>
        </>
      );
    }

    // Page before last
    if (currentPage === totalPages - 1) {
      return (
        <>
          <PaginationItem>
            <PaginationLink onClick={() => handlePageChange(1)} size="sm">
              1
            </PaginationLink>
          </PaginationItem>
          {totalPages > 3 && (
            <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
              <PaginationEllipsis />
            </PaginationItem>
          )}
          <PaginationItem>
            <PaginationLink isActive={true} size="sm">
              {currentPage}
            </PaginationLink>
          </PaginationItem>
          <PaginationItem>
            <PaginationLink onClick={() => handlePageChange(totalPages)} size="sm">
              {totalPages}
            </PaginationLink>
          </PaginationItem>
        </>
      );
    }

    // Middle pages
    return (
      <>
        <PaginationItem>
          <PaginationLink onClick={() => handlePageChange(1)} size="sm">
            1
          </PaginationLink>
        </PaginationItem>
        <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
          <PaginationEllipsis />
        </PaginationItem>
        <PaginationItem>
          <PaginationLink isActive={true} size="sm">
            {currentPage}
          </PaginationLink>
        </PaginationItem>
        <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
          <PaginationEllipsis />
        </PaginationItem>
        <PaginationItem>
          <PaginationLink onClick={() => handlePageChange(totalPages)} size="sm">
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      </>
    );
  }, [currentPage, totalPages, handlePageChange]);

  if (totalPages === 0) {
    return null;
  }

  return (
    <div className={cn('flex w-full items-center justify-end gap-2 align-middle', className)}>
      <p className="text-grey-800 dark:text-grey-100 hidden cursor-default text-sm whitespace-nowrap md:block">
        {`${(currentPage - 1) * itemsPerPage + 1} - ${Math.min(
          currentPage * itemsPerPage,
          totalItems
        )} of ${totalItems}`}
      </p>

      <PaginationRoot>
        <PaginationContent>
          <PaginationItem className={currentPage === 1 ? 'pointer-events-none' : undefined}>
            <PaginationPrevious
              onClick={handlePreviousPage}
              aria-disabled={currentPage === 1}
              aria-label="Go to previous page"
              size="sm"
            />
          </PaginationItem>

          {isMobile ? (
            renderMobilePages()
          ) : (
            <>
              {/* First page */}
              <PaginationItem>
                <PaginationLink
                  isActive={1 === currentPage}
                  onClick={() => handlePageChange(1)}
                  size="sm"
                >
                  1
                </PaginationLink>
              </PaginationItem>

              {/* First ellipsis */}
              {startPage > 2 && (
                <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
                  <PaginationEllipsis />
                </PaginationItem>
              )}

              {[...Array(Math.max(middlePagesNeeded, 0))].map((_, i) => {
                const pageNumber = startPage + i;
                if (pageNumber > endPage) {
                  // Show next available page instead of ellipsis
                  const nextPage = endPage + 1;
                  if (nextPage < totalPages) {
                    return (
                      <PaginationItem key={nextPage}>
                        <PaginationLink onClick={() => handlePageChange(nextPage)} size="sm">
                          {nextPage}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  }
                  return null;
                }
                return (
                  <PaginationItem key={pageNumber}>
                    <PaginationLink
                      isActive={pageNumber === currentPage}
                      onClick={() => handlePageChange(pageNumber)}
                      size="sm"
                    >
                      {pageNumber}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              {/* Second ellipsis */}
              {endPage < totalPages - 1 && (
                <PaginationItem className="w-fit cursor-default hover:bg-transparent dark:hover:bg-transparent">
                  <PaginationEllipsis />
                </PaginationItem>
              )}

              {/* Last page */}
              {totalPages > 1 && (
                <PaginationItem>
                  <PaginationLink
                    isActive={totalPages === currentPage}
                    onClick={() => handlePageChange(totalPages)}
                    size="sm"
                  >
                    {totalPages}
                  </PaginationLink>
                </PaginationItem>
              )}
            </>
          )}

          <PaginationItem
            className={currentPage === totalPages ? 'pointer-events-none' : undefined}
          >
            <PaginationNext
              onClick={handleNextPage}
              aria-disabled={currentPage === totalPages}
              aria-label="Go to next page"
              size="sm"
            />
          </PaginationItem>
        </PaginationContent>
      </PaginationRoot>
    </div>
  );
};

export const TablePagination = React.memo(PaginationComponent);
